import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, Card, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import {
  deleteProcedureByIdWoLoader,
  getPetsCompliantSummaryWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { AppColors } from "../../../../../util/AppColors";
import { getTotalEstimatedCost } from "../../../../../util/function";

const ProcedureCard = ({ procedure, onEdit, onAddItems }) => {
  const dispatch = useDispatch();
  const [showBreakdown, setShowBreakdown] = useState(
    procedure?.priceBreakdownOpen ?? false,
  );
  const [delModVsble, setDelModVsble] = useState(false);
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;
  const mandateDataRaw = sessionStorage.getItem("mandateData");

  const { services, clinicId } = useMemo(() => {
    if (!mandateDataRaw) return { services: [], clinicId: null };
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        services: Array.isArray(reqData?.services) ? reqData.services : [],
        clinicId: reqData?.clinic?.id ?? null,
      };
    } catch {
      return { services: [], clinicId: null };
    }
  }, [mandateDataRaw]);

  const serviceVariant = useMemo(() => {
    const reqServ = services?.find(
      (ser) => ser?.serviceId === procedure?.serviceId,
    );
    const variants = (reqServ?.serviceVariants ?? [])?.filter((vr) =>
      vr?.createdBy ? vr?.createdBy === clinicId : true,
    );
    if (!variants?.length) return null;

    const procVariantId =
      procedure?.procedureVariantId ??
      procedure?.procedureVariantID ??
      procedure?.variantId;
    if (procVariantId != null) {
      const byId = variants?.find(
        (vnt) =>
          vnt?.variantId != null &&
          String(vnt?.variantId) === String(procVariantId),
      );
      if (byId) return byId;
    }

    const byLabel = variants?.find(
      (vnt) =>
        (vnt?.variantName ?? "")?.toLowerCase() ===
        (procedure?.serviceType ?? "")?.toLowerCase(),
    );
    if (byLabel) return byLabel;

    return variants?.[0] ?? null;
  }, [services, clinicId, procedure]);

  const variantServiceCharge = Number(serviceVariant?.serviceCharge ?? 0);
  const variantServicePrice = Number(serviceVariant?.servicePrice ?? 0);

  const consumablesCharge = useMemo(() => {
    if (!serviceVariant) return 0;

    const consumList =
      serviceVariant?.consumList ??
      (serviceVariant?.serviceDetails ?? [])?.filter(
        (li) =>
          (li?.alterType === "N" && !li?.alterName) ||
          (li?.alterType === "Y" && !li?.alterName),
      );

    const presList =
      serviceVariant?.presList ??
      (serviceVariant?.servicePrescreiptions ?? [])?.filter(
        (li) =>
          (li?.administered === "Administered" &&
            li?.alterType === "N" &&
            !li?.alterName) ||
          (li?.alterType === "Y" && !li?.alterName),
      );

    return Number(
      getTotalEstimatedCost([...(consumList ?? []), ...(presList ?? [])]),
    );
  }, [serviceVariant]);

  const { extraBilled, extraItemsCount } = useMemo(() => {
    const billedSeparately = (procedure?.procedureDetails ?? [])?.filter(
      (det) => det?.billSeparated === true,
    );
    return {
      extraBilled: Number(getTotalEstimatedCost(billedSeparately)),
      extraItemsCount: billedSeparately?.length ?? 0,
    };
  }, [procedure?.procedureDetails]);

  const pharmacyRequestsCount = useMemo(
    () =>
      (procedure?.procedureDetails ?? [])?.filter(
        (det) => det?.status === "requested",
      )?.length ?? 0,
    [procedure?.procedureDetails],
  );

  const packageItemsUsedCount = useMemo(
    () =>
      (procedure?.procedureDetails ?? [])?.filter(
        (det) => det?.billSeparated === false && det?.linked === "Y",
      )?.length ?? 0,
    [procedure?.procedureDetails],
  );

  const lineTotal =
    Number(variantServicePrice) * Number(procedure?.quantity ?? 1) +
    Number(extraBilled);

  const onApiCall = () => {
    dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
  };

  const onConfirmDelete = () => {
    if (!procedure?.procedureId) return;
    dispatch(deleteProcedureByIdWoLoader(procedure?.procedureId)).then(
      (res) => {
        if (res?.payload) {
          setDelModVsble(false);
          onApiCall();
        }
      },
    );
  };

  const onCloseDelMod = () => {
    setDelModVsble(false);
  };

  return (
    <>
      <Card
        sx={{
          borderRadius: "8px",
          boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e0e0e0",
          p: 2.5,
          mb: 2,
          backgroundColor: "#ffffff",
        }}
      >
        {/* Top Grid Row */}
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "1fr 1fr",
            sm: "100px 1.5fr 1fr 1fr 1fr 1fr",
          }}
          gap={2}
          alignItems="center"
          mb={2}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#757575",
                letterSpacing: "0.5px",
                mb: 0.5,
                textTransform: "uppercase",
              }}
            >
              CATEGORY
            </Typography>
            <Box
              sx={{
                display: "inline-block",
                backgroundColor: "#e3f2fd",
                color: AppColors.appPrimary || "#0054A6",
                fontWeight: 600,
                fontSize: "12px",
                px: 1.5,
                py: 0.3,
                borderRadius: "12px",
              }}
            >
              {procedure?.category}
            </Box>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#757575",
                letterSpacing: "0.5px",
                mb: 0.5,
                textTransform: "uppercase",
              }}
            >
              PROCEDURE
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: AppColors.appPrimary || "#0054A6",
              }}
            >
              {procedure?.procedureName}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#757575",
                letterSpacing: "0.5px",
                mb: 0.5,
                textTransform: "uppercase",
              }}
            >
              BILLABLE QTY
            </Typography>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#333" }}
            >
              {procedure?.quantity}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#757575",
                letterSpacing: "0.5px",
                mb: 0.5,
                textTransform: "uppercase",
              }}
            >
              PACKAGE PRICE
            </Typography>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#333" }}
            >
              ₹{variantServicePrice}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#757575",
                letterSpacing: "0.5px",
                mb: 0.5,
                textTransform: "uppercase",
              }}
            >
              EXTRA (BILLED)
            </Typography>
            <Typography
              sx={{ fontSize: "14px", fontWeight: 600, color: "#333" }}
            >
              ₹{Number(extraBilled)?.toFixed(2)}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#757575",
                letterSpacing: "0.5px",
                mb: 0.5,
                textTransform: "uppercase",
              }}
            >
              LINE TOTAL
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 700,
                color: "#212121",
              }}
            >
              ₹{Number(lineTotal)?.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        {/* Middle Controls Row: Price breakdown toggle + Always visible Badges + Action Buttons */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          gap={1.5}
          pt={1.5}
          sx={{ borderTop: "1px solid #f0f0f0" }}
        >
          {/* Left Side: Toggle link + Badges */}
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
            <Box
              onClick={() => setShowBreakdown(!showBreakdown)}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
                color: AppColors.appPrimary || "#0054A6",
                fontWeight: 600,
                fontSize: "13px",
                mr: 1,
              }}
            >
              {showBreakdown ? (
                <ArrowDropDownIcon sx={{ fontSize: 20 }} />
              ) : (
                <ArrowRightIcon sx={{ fontSize: 20 }} />
              )}
              Price breakdown
            </Box>

            {packageItemsUsedCount > 0 ? (
              <Box
                sx={{
                  backgroundColor: "#e8f5e9",
                  color: "#2e7d32",
                  fontSize: "12px",
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "16px",
                  border: "1px solid #c8e6c9",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <span>✓</span> {packageItemsUsedCount} item
                {packageItemsUsedCount === 1 ? "" : "s"} used from package — no
                extra charge
              </Box>
            ) : null}

            <Box
              sx={{
                backgroundColor: "#fff8e1",
                color: "#e65100",
                fontSize: "12px",
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                borderRadius: "16px",
                border: "1px solid #ffe082",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <span>+</span> {extraItemsCount} extra item
              {extraItemsCount === 1 ? "" : "s"} billed separately - ₹
              {Number(extraBilled)?.toFixed(2)}
            </Box>

            {pharmacyRequestsCount > 0 ? (
              <Box
                sx={{
                  backgroundColor: "#e8eaf6",
                  color: "#283593",
                  fontSize: "12px",
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "16px",
                  border: "1px solid #c5cae9",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <span>📦</span> {pharmacyRequestsCount} item
                {pharmacyRequestsCount === 1 ? "" : "s"} requested from pharmacy
              </Box>
            ) : null}
          </Box>

          {/* Right Side: Actions */}
          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            ml={{ xs: 0, md: "auto" }}
          >
            <Button
              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
              onClick={() => onEdit(procedure)}
              sx={{
                color: AppColors.appPrimary || "#0054A6",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                minWidth: "auto",
                p: "4px 8px",
                whiteSpace: "nowrap",
              }}
            >
              Edit
            </Button>

            <Button
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => onAddItems(procedure)}
              sx={{
                color: AppColors.appPrimary || "#0054A6",
                borderColor: AppColors.appPrimary || "#0054A6",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                borderRadius: "6px",
                px: 1.5,
                py: 0.5,
                whiteSpace: "nowrap",
                height: "32px",
                "&:hover": {
                  borderColor: AppColors.appPrimary || "#0054A6",
                  backgroundColor: "rgba(0, 84, 166, 0.04)",
                },
              }}
            >
              Add items
            </Button>

            <Button
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
              onClick={() => setDelModVsble(true)}
              sx={{
                color: "#d32f2f",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "13px",
                minWidth: "auto",
                p: "4px 8px",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "rgba(211, 47, 47, 0.04)",
                },
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>

        {/* Expandable Price Breakdown Sub-Panel */}
        {showBreakdown && (
          <Box
            sx={{
              pt: 1.5,
              mt: 1.5,
              borderTop: "1px dashed #e0e0e0",
            }}
          >
            <Typography sx={{ fontSize: "13px", color: "#616161", mb: 0.5 }}>
              Service Charge:{" "}
              <span style={{ fontWeight: 700, color: "#212121" }}>
                ₹{variantServiceCharge}
              </span>
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#616161", mb: 0.5 }}>
              BOM Charge (materials bundled into the package):{" "}
              <span style={{ fontWeight: 700, color: "#212121" }}>
                ₹{Number(consumablesCharge)?.toFixed(2)}
              </span>
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#616161" }}>
              Package Price (fixed, does not change with actual usage):{" "}
              <span style={{ fontWeight: 700, color: "#212121" }}>
                ₹{variantServicePrice}
              </span>
            </Typography>
          </Box>
        )}
      </Card>

      <CustomModal
        open={delModVsble}
        onClose={onCloseDelMod}
        header=""
        modal
        modalWidth={30}
        headerShown={false}
      >
        <div className="flex-center">
          <Typography className="txt-semi-bold fs16 text-align-center mv20 orange4">
            Are you sure you want to delete this record?
          </Typography>
        </div>
        <div className="flex-row-ali-cen flex-center pb20">
          <div>
            <CustomButton
              text="Yes"
              redBtn
              smallBtn
              onClick={onConfirmDelete}
            />
          </div>
          <div className="ml20">
            <CustomButton text="No" smallBtn onClick={onCloseDelMod} />
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default ProcedureCard;
